import { useState, useEffect, useRef, useCallback } from 'react'
import { C, font, cardStyle } from '../design'
import {
  createMentorSession, listMentorSessions, getMentorSession,
  deleteMentorSession, mentorChatStart, mentorCouncilStart,
  mentorExtractFile, pollMentorJob,
} from '../utils/api'

// ─── constants ───────────────────────────────────────────────────────────────

const ROLES = [
  { id: 'psychologist', label: 'Психолог',  icon: '◉', desc: 'Паттерны, убеждения, эмоции, отношения с собой' },
  { id: 'coach',        label: 'Коуч',      icon: '◈', desc: 'Цели, стратегии, конкретные шаги к результату' },
  { id: 'trainer',      label: 'Тренер',    icon: '◆', desc: 'Привычки, дисциплина, раскрытие потенциала' },
  { id: 'council',      label: '⬡ Совет',   icon: '⬡', desc: 'Пять независимых советников + синтез Председателя' },
]

const ADVISOR_NAMES = {
  contrarian:       'The Contrarian',
  first_principles: 'The First Principles Thinker',
  expansionist:     'The Expansionist',
  outsider:         'The Outsider',
  executor:         'The Executor',
  chairman:         'Председатель',
}

const ADVISOR_ORDER = ['contrarian','first_principles','expansionist','outsider','executor']

const ROLE_LABELS = { psychologist: 'Психолог', coach: 'Коуч', trainer: 'Тренер', council: 'Совет' }

// ─── lightweight markdown (bold/italic/code/lists/headings/hr) ────────────────
// Chat replies come back as markdown text (**bold**, - lists, --- rules) but were
// rendered as raw strings — this is a compact renderer scoped to chat bubbles,
// not a full markdown library (not worth the bundle weight for short messages).

function renderInlineMd(text, keyPrefix) {
  const parts = []
  let remaining = text
  let key = 0
  const re = /\*\*(.+?)\*\*|`(.+?)`|\*(.+?)\*|_(.+?)_/
  while (remaining.length) {
    const m = remaining.match(re)
    if (!m) { parts.push(remaining); break }
    if (m.index > 0) parts.push(remaining.slice(0, m.index))
    if (m[1] !== undefined) parts.push(<strong key={`${keyPrefix}-${key++}`}>{m[1]}</strong>)
    else if (m[2] !== undefined) parts.push(
      <code key={`${keyPrefix}-${key++}`} style={{
        background: 'rgba(255,255,255,0.08)', padding: '1px 5px', borderRadius: 4,
        fontFamily: font.mono, fontSize: '0.9em',
      }}>{m[2]}</code>
    )
    else if (m[3] !== undefined) parts.push(<em key={`${keyPrefix}-${key++}`}>{m[3]}</em>)
    else if (m[4] !== undefined) parts.push(<em key={`${keyPrefix}-${key++}`}>{m[4]}</em>)
    remaining = remaining.slice(m.index + m[0].length)
  }
  return parts
}

function MarkdownText({ text }) {
  if (!text) return null
  const lines = text.split('\n')
  const blocks = []
  let listItems = null
  let listType = null

  function flushList() {
    if (!listItems) return
    const Tag = listType === 'ol' ? 'ol' : 'ul'
    blocks.push(
      <Tag key={`list-${blocks.length}`} style={{ margin: '6px 0', paddingLeft: 20 }}>
        {listItems.map((it, i) => (
          <li key={i} style={{ marginBottom: 2 }}>{renderInlineMd(it, `li-${blocks.length}-${i}`)}</li>
        ))}
      </Tag>
    )
    listItems = null
    listType = null
  }

  lines.forEach((line, i) => {
    const trimmed = line.trim()
    if (/^(-{3,}|\*{3,})$/.test(trimmed)) {
      flushList()
      blocks.push(<hr key={`hr-${i}`} style={{ border: 'none', borderTop: `1px solid ${C.border}`, margin: '10px 0' }} />)
      return
    }
    const heading = trimmed.match(/^(#{1,4})\s+(.*)$/)
    if (heading) {
      flushList()
      blocks.push(
        <div key={`h-${i}`} style={{ fontWeight: 700, fontSize: heading[1].length <= 2 ? 16 : 15, margin: '8px 0 4px' }}>
          {renderInlineMd(heading[2], `h-${i}`)}
        </div>
      )
      return
    }
    const bullet = trimmed.match(/^[-*]\s+(.*)$/)
    if (bullet) {
      if (listType !== 'ul') { flushList(); listType = 'ul'; listItems = [] }
      listItems.push(bullet[1])
      return
    }
    const numbered = trimmed.match(/^\d+\.\s+(.*)$/)
    if (numbered) {
      if (listType !== 'ol') { flushList(); listType = 'ol'; listItems = [] }
      listItems.push(numbered[1])
      return
    }
    flushList()
    if (trimmed === '') {
      blocks.push(<div key={`sp-${i}`} style={{ height: 8 }} />)
    } else {
      blocks.push(<div key={`p-${i}`}>{renderInlineMd(line, `p-${i}`)}</div>)
    }
  })
  flushList()
  return <>{blocks}</>
}

function useIsMobile() {
  const [m, setM] = useState(window.innerWidth < 768)
  useEffect(() => {
    const h = () => setM(window.innerWidth < 768)
    window.addEventListener('resize', h)
    return () => window.removeEventListener('resize', h)
  }, [])
  return m
}

// ─── sub-components ──────────────────────────────────────────────────────────

function RoleTab({ role, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      background: active ? 'rgba(184,189,212,0.12)' : 'transparent',
      border: `1px solid ${active ? C.borderHi : 'transparent'}`,
      borderRadius: 8, padding: '6px 14px',
      color: active ? C.silverHi : C.silverLo,
      fontFamily: font.body, fontSize: 13, cursor: 'pointer',
      flexShrink: 0, transition: 'all .15s',
      fontWeight: active ? 600 : 400,
    }}>
      {role.icon} {role.label}
    </button>
  )
}

function SessionItem({ session, active, onClick, onDelete }) {
  const roleLabel = ROLE_LABELS[session.role] || session.role
  return (
    <div onClick={onClick} style={{
      padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
      background: active ? 'rgba(184,189,212,0.1)' : 'transparent',
      border: `1px solid ${active ? C.borderHi : 'transparent'}`,
      display: 'flex', alignItems: 'flex-start', gap: 8,
      transition: 'all .15s', position: 'relative',
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, color: C.silverLo, fontFamily: font.mono, marginBottom: 2 }}>
          {roleLabel}
        </div>
        <div style={{
          fontSize: 13, color: C.silverHi, fontFamily: font.body,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {session.title || 'Новая сессия'}
        </div>
      </div>
      <button onClick={e => { e.stopPropagation(); onDelete() }} style={{
        background: 'none', border: 'none', color: C.silverLo, cursor: 'pointer',
        fontSize: 14, padding: '0 2px', opacity: 0.5,
        ':hover': { opacity: 1 },
      }}>✕</button>
    </div>
  )
}

function ChatMessage({ msg, isMobile }) {
  const isUser = msg.role === 'user'
  const isAdvisor = ADVISOR_ORDER.includes(msg.role)
  const isReview = msg.role.startsWith('peer_review_')
  const isChairman = msg.role === 'chairman'
  const isAssistant = msg.role === 'assistant'

  if (isAdvisor || isReview || isChairman) return null // rendered in CouncilView

  return (
    <div style={{
      display: 'flex', flexDirection: isUser ? 'row-reverse' : 'row',
      gap: 10, marginBottom: 16, alignItems: 'flex-start',
    }}>
      {!isUser && (
        <div style={{
          width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
          background: 'rgba(184,189,212,0.1)', border: `1px solid ${C.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 14, color: C.silverLo,
        }}>◈</div>
      )}
      <div style={{
        maxWidth: isMobile ? '90%' : '72%',
        background: isUser ? 'rgba(184,189,212,0.08)' : 'rgba(255,255,255,0.03)',
        border: `1px solid ${isUser ? C.border : 'rgba(184,189,212,0.08)'}`,
        borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
        padding: '12px 16px', fontFamily: font.body, fontSize: 14,
        color: C.text, lineHeight: 1.6,
        wordBreak: 'break-word',
      }}>
        <MarkdownText text={msg.content} />
      </div>
    </div>
  )
}

function AdvisorCard({ advisorKey, content, letterMap }) {
  const [open, setOpen] = useState(true)
  const name = ADVISOR_NAMES[advisorKey] || advisorKey
  const letter = letterMap ? letterMap[advisorKey] : ''
  return (
    <div style={{
      ...cardStyle,
      marginBottom: 12, border: `1px solid ${C.border}`, borderRadius: 12,
    }}>
      <button onClick={() => setOpen(o => !o)} style={{
        background: 'none', border: 'none', cursor: 'pointer', width: '100%',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 16px', color: C.silverHi, fontFamily: font.body,
      }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            fontFamily: font.mono, fontSize: 11, color: C.silverLo,
            background: 'rgba(184,189,212,0.08)', borderRadius: 4, padding: '2px 6px',
          }}>{letter}</span>
          <span style={{ fontSize: 14, fontWeight: 600 }}>{name}</span>
        </span>
        <span style={{ fontSize: 12, color: C.silverLo }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{
          padding: '0 16px 14px', fontSize: 14, color: C.text,
          fontFamily: font.body, lineHeight: 1.65,
          borderTop: `1px solid ${C.border}`, paddingTop: 12,
        }}>
          <MarkdownText text={content} />
        </div>
      )}
    </div>
  )
}

function CouncilView({ messages, progress }) {
  const advisorMsgs = {}
  const peerMsgs = {}
  let chairmanMsg = null

  for (const m of messages) {
    if (ADVISOR_ORDER.includes(m.role)) advisorMsgs[m.role] = m.content
    else if (m.role.startsWith('peer_review_')) peerMsgs[m.role] = m.content
    else if (m.role === 'chairman') chairmanMsg = m.content
  }

  // Build letter map from available advisor order (stable)
  const letterMap = {}
  ADVISOR_ORDER.forEach((k, i) => { letterMap[k] = String.fromCharCode(65 + i) })

  const phase = progress?.phase || 'done'
  const completed = progress?.completed || 0
  const total = progress?.total || 11

  return (
    <div>
      {/* Progress */}
      {phase !== 'done' && (
        <div style={{
          padding: '12px 16px', marginBottom: 20, borderRadius: 10,
          background: 'rgba(184,189,212,0.05)', border: `1px solid ${C.border}`,
          fontFamily: font.body, fontSize: 13, color: C.silverLo,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span>
              {phase === 'advisors' ? '◉ Советники анализируют...' :
               phase === 'peer_review' ? '◈ Взаимные рецензии...' :
               '⬡ Председатель синтезирует...'}
            </span>
            <span style={{ fontFamily: font.mono }}>{completed}/{total}</span>
          </div>
          <div style={{
            height: 3, background: 'rgba(184,189,212,0.1)', borderRadius: 2,
          }}>
            <div style={{
              height: '100%', borderRadius: 2,
              width: `${Math.round((completed / total) * 100)}%`,
              background: C.silverHi, transition: 'width .4s',
            }} />
          </div>
        </div>
      )}

      {/* Advisors */}
      {Object.keys(advisorMsgs).length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 11, fontFamily: font.mono, color: C.silverLo, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>
            Советники
          </div>
          {ADVISOR_ORDER.filter(k => advisorMsgs[k]).map(k => (
            <AdvisorCard key={k} advisorKey={k} content={advisorMsgs[k]} letterMap={letterMap} />
          ))}
        </div>
      )}

      {/* Peer reviews */}
      {Object.keys(peerMsgs).length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 11, fontFamily: font.mono, color: C.silverLo, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>
            Взаимные рецензии
          </div>
          {Object.entries(peerMsgs).map(([role, content]) => {
            const letter = role.replace('peer_review_', '')
            return (
              <div key={role} style={{
                ...cardStyle, marginBottom: 10, border: `1px solid ${C.border}`,
                borderRadius: 10, padding: '12px 16px',
              }}>
                <div style={{ fontSize: 11, fontFamily: font.mono, color: C.silverLo, marginBottom: 6 }}>
                  Рецензия советника {letter}
                </div>
                <div style={{ fontSize: 13, color: C.text, fontFamily: font.body, lineHeight: 1.6 }}>
                  <MarkdownText text={content} />
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Chairman verdict */}
      {chairmanMsg && (
        <div style={{
          border: `1px solid rgba(184,189,212,0.3)`,
          borderRadius: 14, overflow: 'hidden', marginBottom: 16,
        }}>
          <div style={{
            padding: '14px 20px',
            background: 'rgba(184,189,212,0.06)',
            borderBottom: `1px solid ${C.border}`,
            fontFamily: font.body, fontSize: 14, fontWeight: 700,
            color: C.silverHi, display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <span>⬡</span> Вердикт Председателя
          </div>
          <div style={{
            padding: '16px 20px', fontSize: 14, color: C.text,
            fontFamily: font.body, lineHeight: 1.7,
          }}>
            <MarkdownText text={chairmanMsg} />
          </div>
        </div>
      )}
    </div>
  )
}

// ─── main component ───────────────────────────────────────────────────────────

export default function Mentor({ userId, isOwner }) {
  const isMobile = useIsMobile()
  const [activeRole, setActiveRole] = useState('psychologist')
  const [sessions, setSessions] = useState([])
  const [currentSession, setCurrentSession] = useState(null)
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [loadingSessions, setLoadingSessions] = useState(true)
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile)
  const [councilProgress, setCouncilProgress] = useState(null)
  const [attachedFile, setAttachedFile] = useState(null) // { name, text }
  const [extractingFile, setExtractingFile] = useState(false)
  const [error, setError] = useState(null)
  const messagesEndRef = useRef(null)
  const fileInputRef = useRef(null)
  const councilInputRef = useRef(null)
  const pollRef = useRef(null)
  const inputRef = useRef(null)

  // If session has a pending user message (no reply yet) — watch DB until reply appears
  const watchForReply = useCallback((sessionId) => {
    const token = 'watch_' + sessionId + '_' + Date.now()
    pollRef.current = token
    setLoading(true)
    const watch = async () => {
      if (pollRef.current !== token) return
      try {
        const full = await getMentorSession(sessionId)
        const msgs = full.messages || []
        const hasReply = msgs.some(m => m.role === 'assistant' || m.role === 'chairman')
        if (hasReply) {
          setMessages(msgs)
          setLoading(false)
          inputRef.current?.focus()
          loadSessions()
          return
        }
        setTimeout(watch, 3000)
      } catch {
        setTimeout(watch, 3000)
      }
    }
    setTimeout(watch, 3000)
  }, []) // eslint-disable-line

  // Load sessions list; autoOpen=true opens the most recent session with messages automatically
  const loadSessions = useCallback(async (autoOpen = false) => {
    try {
      setLoadingSessions(true)
      const list = await listMentorSessions(userId)
      setSessions(list)
      if (autoOpen && list.length > 0) {
        // Find most recent session that has at least one message
        let target = list[0] // fallback: most recent
        const s = list[0]
        setCurrentSession(s)
        setActiveRole(s.role)
        const full = await getMentorSession(s.id)
        const msgs = full.messages || []
        setMessages(msgs)
        // If last message is from user with no reply — resume watching
        const lastMsg = msgs[msgs.length - 1]
        if (lastMsg && lastMsg.role === 'user') {
          watchForReply(s.id)
        }
      }
    } catch (e) {
      console.error('loadSessions', e)
    } finally {
      setLoadingSessions(false)
    }
  }, [userId, watchForReply])

  useEffect(() => {
    loadSessions(true)
    return () => { pollRef.current = null } // stop any watches on unmount
  }, [loadSessions])

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Auto-focus input when session opens or changes
  useEffect(() => {
    if (currentSession && !loading) {
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [currentSession?.id])

  // Open session
  const openSession = async (session) => {
    try {
      setCurrentSession(session)
      setActiveRole(session.role)
      setError(null)
      const full = await getMentorSession(session.id)
      setMessages(full.messages || [])
      if (isMobile) setSidebarOpen(false)
    } catch (e) {
      setError('Не удалось загрузить сессию')
    }
  }

  // Create new session
  const startNewSession = async (role) => {
    try {
      setError(null)
      const session = await createMentorSession(userId, role)
      setSessions(prev => [session, ...prev])
      setCurrentSession(session)
      setMessages([])
      setActiveRole(role)
      setAttachedFile(null)
      if (isMobile) setSidebarOpen(false)
    } catch (e) {
      setError('Не удалось создать сессию')
    }
  }

  // Delete session
  const handleDeleteSession = async (sessionId) => {
    try {
      await deleteMentorSession(sessionId)
      setSessions(prev => prev.filter(s => s.id !== sessionId))
      if (currentSession?.id === sessionId) {
        setCurrentSession(null)
        setMessages([])
      }
    } catch (e) {
      setError('Не удалось удалить сессию')
    }
  }

  // File upload
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''
    setExtractingFile(true)
    setError(null)
    try {
      const result = await mentorExtractFile(file)
      setAttachedFile({ name: file.name, text: result.text, chars: result.chars })
    } catch (ex) {
      setError('Не удалось извлечь текст из файла')
    } finally {
      setExtractingFile(false)
    }
  }

  // Poll council progress
  const pollCouncilProgress = (jobId, sessionId) => {
    pollRef.current = jobId
    const poll = async () => {
      if (pollRef.current !== jobId) return
      try {
        const res = await fetch(`${import.meta.env.VITE_API_URL}/api/analyze/job/${jobId}`)
        if (!res.ok) { setTimeout(poll, 2000); return }
        const data = await res.json()
        if (data.status === 'running' || data.status === 'pending') {
          setCouncilProgress({
            phase: data.phase || 'advisors',
            completed: data.completed || 0,
            total: data.total || 11,
          })
          // Also refresh messages to show partial results
          const full = await getMentorSession(sessionId)
          setMessages(full.messages || [])
          setTimeout(poll, 3000)
        } else if (data.status === 'done') {
          setCouncilProgress(null)
          setLoading(false)
          const full = await getMentorSession(sessionId)
          setMessages(full.messages || [])
          loadSessions()
        } else if (data.status === 'error') {
          setLoading(false)
          setCouncilProgress(null)
          setError('Ошибка совета: ' + (data.error || 'неизвестная ошибка'))
        }
      } catch (ex) {
        setTimeout(poll, 3000)
      }
    }
    setTimeout(poll, 2000)
  }

  // Send chat message
  const sendMessage = async () => {
    const msg = input.trim()
    if (!msg && !attachedFile) return
    if (!currentSession) return
    if (loading) return
    setLoading(true)
    setError(null)
    const fileText = attachedFile?.text || null
    setInput('')
    setAttachedFile(null)
    inputRef.current?.focus()

    // Optimistic: show user message immediately
    const tempId = Date.now()
    const userDisplayMsg = fileText
      ? msg + (msg ? '\n\n' : '') + `[Файл: ${attachedFile.name}]`
      : msg
    setMessages(prev => [...prev, { id: tempId, role: 'user', content: userDisplayMsg, created_at: new Date().toISOString() }])

    try {
      const { job_id } = await mentorChatStart(currentSession.id, userId, activeRole, msg, fileText)
      // Poll
      const poll = async () => {
        if (pollRef.current === 'chat_' + tempId) {
          try {
            const res = await fetch(`${import.meta.env.VITE_API_URL}/api/analyze/job/${job_id}`)
            const data = await res.json()
            if (data.status === 'done') {
              // Reload full session to get real messages with DB ids
              const full = await getMentorSession(currentSession.id)
              setMessages(full.messages || [])
              setLoading(false)
              inputRef.current?.focus()
              loadSessions()
            } else if (data.status === 'error') {
              setError('Ошибка наставника: ' + (data.error || ''))
              setLoading(false)
              inputRef.current?.focus()
            } else {
              setTimeout(poll, 2000)
            }
          } catch (ex) {
            setTimeout(poll, 2000)
          }
        }
      }
      pollRef.current = 'chat_' + tempId
      setTimeout(poll, 2000)
    } catch (e) {
      setError('Не удалось отправить сообщение: ' + e.message)
      setLoading(false)
      inputRef.current?.focus()
    }
  }

  // Start council
  const startCouncil = async () => {
    const question = (councilInputRef.current?.value || input).trim()
    if (!question && !attachedFile) return
    if (loading) return
    setLoading(true)
    setError(null)
    const fileText = attachedFile?.text || null
    setInput('')
    setAttachedFile(null)
    if (councilInputRef.current) councilInputRef.current.value = ''

    try {
      const { job_id, session_id } = await mentorCouncilStart(userId, question, fileText, currentSession?.role === 'council' ? currentSession?.id : null)
      // Create/update session
      const session = { id: session_id, role: 'council', title: question.slice(0, 60) }
      setCurrentSession(session)
      setMessages([{ id: Date.now(), role: 'user', content: question + (fileText && attachedFile ? `\n\n[Файл: ${attachedFile.name}]` : ''), created_at: new Date().toISOString() }])
      setCouncilProgress({ phase: 'advisors', completed: 0, total: 11 })
      pollCouncilProgress(job_id, session_id)
      loadSessions()
    } catch (e) {
      setError('Не удалось запустить совет: ' + e.message)
      setLoading(false)
      setCouncilProgress(null)
    }
  }

  // Export session to markdown
  const exportMarkdown = () => {
    if (!currentSession || !messages.length) return
    const roleLabel = ROLE_LABELS[currentSession.role] || currentSession.role
    let md = `# ${currentSession.title || 'Сессия наставника'}\n\n`
    md += `**Режим:** ${roleLabel}  \n`
    md += `**Дата:** ${new Date(currentSession.created_at).toLocaleDateString('ru')}\n\n---\n\n`

    for (const m of messages) {
      if (m.role === 'user') {
        md += `**Пользователь:**\n\n${m.content}\n\n---\n\n`
      } else if (m.role === 'assistant') {
        md += `**Наставник:**\n\n${m.content}\n\n---\n\n`
      } else if (ADVISOR_ORDER.includes(m.role)) {
        md += `**${ADVISOR_NAMES[m.role]}:**\n\n${m.content}\n\n`
      } else if (m.role.startsWith('peer_review_')) {
        md += `**Рецензия ${m.role.replace('peer_review_', '')}:**\n\n${m.content}\n\n`
      } else if (m.role === 'chairman') {
        md += `---\n\n## Вердикт Председателя\n\n${m.content}\n\n`
      }
    }

    const blob = new Blob([md], { type: 'text/markdown' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `mentor-${currentSession.id}-${Date.now()}.md`
    a.click()
    URL.revokeObjectURL(url)
  }

  const isCouncilMode = activeRole === 'council'
  const hasMessages = messages.some(m => m.role !== 'user' || true)
  const userMessagesInCouncil = messages.filter(m => m.role === 'user')
  const councilMessages = messages.filter(m => m.role !== 'user')

  // ─── render ───────────────────────────────────────────────────────────────

  return (
    <div style={{
      display: 'flex', height: '100%', overflow: 'hidden',
      fontFamily: font.body, color: C.text,
    }}>

      {/* Sidebar */}
      {(sidebarOpen || !isMobile) && (
        <div style={{
          width: isMobile ? '100%' : 240, flexShrink: 0,
          borderRight: `1px solid ${C.border}`,
          display: 'flex', flexDirection: 'column',
          background: 'rgba(5,5,13,0.6)',
          position: isMobile ? 'absolute' : 'relative',
          zIndex: isMobile ? 100 : 'auto',
          height: '100%', overflowY: 'auto',
        }}>
          <div style={{ padding: '14px 12px 8px', borderBottom: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 12, fontFamily: font.mono, color: C.silverLo, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>
              Новая сессия
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {ROLES.map(r => (
                <button key={r.id} onClick={() => startNewSession(r.id)} style={{
                  background: 'rgba(184,189,212,0.04)', border: `1px solid ${C.border}`,
                  borderRadius: 8, padding: '7px 10px', cursor: 'pointer',
                  color: C.silverHi, fontFamily: font.body, fontSize: 12,
                  textAlign: 'left', transition: 'all .15s',
                  display: 'flex', alignItems: 'center', gap: 7,
                }}>
                  <span style={{ fontSize: 14 }}>{r.icon}</span>
                  <div>
                    <div style={{ fontWeight: 600 }}>{r.label}</div>
                    <div style={{ fontSize: 10, color: C.silverLo, marginTop: 1 }}>{r.desc}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div style={{ padding: '10px 12px', flex: 1 }}>
            <div style={{ fontSize: 11, fontFamily: font.mono, color: C.silverLo, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>
              История
            </div>
            {loadingSessions ? (
              <div style={{ fontSize: 12, color: C.silverLo, padding: '4px 0' }}>Загрузка...</div>
            ) : sessions.length === 0 ? (
              <div style={{ fontSize: 12, color: C.silverLo, padding: '4px 0' }}>Нет сессий</div>
            ) : sessions.map(s => (
              <SessionItem
                key={s.id}
                session={s}
                active={currentSession?.id === s.id}
                onClick={() => openSession(s)}
                onDelete={() => handleDeleteSession(s.id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Main area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{
          padding: '10px 16px', borderBottom: `1px solid ${C.border}`,
          display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0,
          background: 'rgba(5,5,13,0.4)', backdropFilter: 'blur(8px)',
          flexWrap: 'wrap', rowGap: 6,
        }}>
          {isMobile && (
            <button onClick={() => setSidebarOpen(o => !o)} style={{
              background: 'none', border: `1px solid ${C.border}`,
              borderRadius: 6, padding: '4px 8px', color: C.silverLo,
              cursor: 'pointer', fontSize: 13,
            }}>☰</button>
          )}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', flex: 1 }}>
            {ROLES.map(r => (
              <RoleTab
                key={r.id}
                role={r}
                active={activeRole === r.id}
                onClick={() => setActiveRole(r.id)}
              />
            ))}
          </div>
          {currentSession && messages.length > 0 && (
            <button onClick={exportMarkdown} style={{
              background: 'none', border: `1px solid ${C.border}`,
              borderRadius: 8, padding: '5px 10px', color: C.silverLo,
              cursor: 'pointer', fontSize: 12, fontFamily: font.mono,
            }}>↓ md</button>
          )}
        </div>

        {/* Empty state */}
        {!currentSession && (
          <div style={{
            flex: 1, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', padding: 40,
            gap: 12,
          }}>
            <div style={{ fontSize: 32, opacity: 0.3 }}>◈</div>
            <div style={{ fontSize: 16, color: C.silverLo, textAlign: 'center' }}>
              Выберите режим и начните сессию
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginTop: 8 }}>
              {ROLES.map(r => (
                <button key={r.id} onClick={() => startNewSession(r.id)} style={{
                  background: 'rgba(184,189,212,0.06)', border: `1px solid ${C.border}`,
                  borderRadius: 10, padding: '8px 16px', cursor: 'pointer',
                  color: C.silverHi, fontFamily: font.body, fontSize: 13,
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  <span>{r.icon}</span> {r.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Council mode */}
        {currentSession && isCouncilMode && (
          <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '16px 12px' : '20px 28px' }}>
            {/* Council question input (if no messages yet or after verdict) */}
            {(!loading || councilProgress) && (
              <div style={{
                ...cardStyle,
                border: `1px solid ${C.border}`, borderRadius: 14,
                padding: 20, marginBottom: 24,
              }}>
                <div style={{ fontSize: 13, color: C.silverLo, marginBottom: 10, fontFamily: font.mono }}>
                  Вопрос для совета
                </div>
                <textarea
                  ref={councilInputRef}
                  placeholder="Опишите ситуацию или решение которое нужно обдумать..."
                  defaultValue=""
                  disabled={loading}
                  style={{
                    width: '100%', minHeight: 100, background: 'rgba(255,255,255,0.03)',
                    border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px',
                    color: C.text, fontFamily: font.body, fontSize: 14, lineHeight: 1.6,
                    resize: 'vertical', boxSizing: 'border-box',
                  }}
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input ref={fileInputRef} type="file" accept=".md,.txt,.pdf" style={{ display: 'none' }} onChange={handleFileChange} />
                  <button onClick={() => fileInputRef.current?.click()} disabled={loading || extractingFile} style={{
                    background: 'none', border: `1px solid ${C.border}`, borderRadius: 8,
                    padding: '6px 12px', cursor: 'pointer', color: C.silverLo,
                    fontFamily: font.body, fontSize: 12,
                  }}>
                    {extractingFile ? '⟳ Читаю...' : '⊕ Файл'}
                  </button>
                  {attachedFile && (
                    <span style={{ fontSize: 12, color: C.silverLo }}>
                      {attachedFile.name} ({Math.round(attachedFile.chars / 1000)}к символов)
                      <button onClick={() => setAttachedFile(null)} style={{ background: 'none', border: 'none', color: C.silverLo, cursor: 'pointer', marginLeft: 4 }}>✕</button>
                    </span>
                  )}
                  <div style={{ flex: 1 }} />
                  <button onClick={startCouncil} disabled={loading} style={{
                    background: loading ? 'rgba(184,189,212,0.05)' : 'rgba(184,189,212,0.1)',
                    border: `1px solid ${loading ? C.border : C.borderHi}`,
                    borderRadius: 10, padding: '8px 20px', cursor: loading ? 'default' : 'pointer',
                    color: loading ? C.silverLo : C.silverHi,
                    fontFamily: font.body, fontSize: 14, fontWeight: 600,
                  }}>
                    {loading ? '⟳ Идёт совет...' : '⬡ Созвать совет'}
                  </button>
                </div>
              </div>
            )}

            {/* Council results */}
            <CouncilView messages={messages} progress={councilProgress} />
            <div ref={messagesEndRef} />
          </div>
        )}

        {/* Regular chat mode */}
        {currentSession && !isCouncilMode && (
          <>
            <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '16px 12px' : '20px 28px' }}>
              {messages.length === 0 && !loading && (
                <div style={{
                  padding: '24px', textAlign: 'center',
                  fontFamily: font.body, fontSize: 14, color: C.silverLo,
                }}>
                  {ROLES.find(r => r.id === activeRole)?.desc}
                </div>
              )}
              {messages.map(m => (
                <ChatMessage key={m.id} msg={m} isMobile={isMobile} />
              ))}
              {loading && (
                <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'flex-start' }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: '50%',
                    background: 'rgba(184,189,212,0.1)', border: `1px solid ${C.border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 14, color: C.silverLo,
                  }}>◈</div>
                  <div style={{
                    padding: '12px 16px', borderRadius: '16px 16px 16px 4px',
                    background: 'rgba(255,255,255,0.03)', border: `1px solid rgba(184,189,212,0.08)`,
                    fontFamily: font.body, fontSize: 14, color: C.silverLo,
                  }}>
                    <span style={{ animation: 'pulse 1.2s infinite' }}>● ● ●</span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input area */}
            <div style={{
              borderTop: `1px solid ${C.border}`, padding: '12px 16px',
              flexShrink: 0, background: 'rgba(5,5,13,0.6)',
            }}>
              {error && (
                <div style={{ color: '#e07070', fontSize: 12, marginBottom: 8, fontFamily: font.body }}>
                  {error}
                </div>
              )}
              {attachedFile && (
                <div style={{
                  fontSize: 12, color: C.silverLo, marginBottom: 8, padding: '5px 10px',
                  background: 'rgba(184,189,212,0.06)', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  <span>⊕ {attachedFile.name}</span>
                  <span style={{ color: C.silverLo, opacity: 0.6 }}>({Math.round(attachedFile.chars / 1000)}к симв.)</span>
                  <button onClick={() => setAttachedFile(null)} style={{ background: 'none', border: 'none', color: C.silverLo, cursor: 'pointer', marginLeft: 'auto' }}>✕</button>
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <input type="file" ref={fileInputRef} accept=".md,.txt,.pdf" style={{ display: 'none' }} onChange={handleFileChange} />
                <button onClick={() => fileInputRef.current?.click()} disabled={loading || extractingFile} title="Прикрепить файл (.md, .txt, .pdf)" style={{
                  background: 'none', border: `1px solid ${C.border}`, borderRadius: 8,
                  width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', color: C.silverLo, fontSize: 16, flexShrink: 0,
                }}>
                  {extractingFile ? '⟳' : '⊕'}
                </button>
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage() }
                  }}
                  placeholder="Напишите сообщение... (Enter — отправить, Shift+Enter — новая строка)"
                  rows={1}
                  style={{
                    flex: 1, background: 'rgba(255,255,255,0.04)',
                    border: `1px solid ${C.border}`, borderRadius: 10, padding: '8px 12px',
                    color: C.text, fontFamily: font.body, fontSize: 14, lineHeight: 1.5,
                    resize: 'none', minHeight: 36, maxHeight: 140, overflowY: 'auto',
                    outline: 'none',
                  }}
                />
                <button onClick={sendMessage} disabled={loading || (!input.trim() && !attachedFile)} style={{
                  background: (loading || (!input.trim() && !attachedFile)) ? 'rgba(184,189,212,0.05)' : 'rgba(184,189,212,0.12)',
                  border: `1px solid ${(loading || (!input.trim() && !attachedFile)) ? C.border : C.borderHi}`,
                  borderRadius: 10, width: 36, height: 36, display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  cursor: (loading || (!input.trim() && !attachedFile)) ? 'default' : 'pointer',
                  color: (loading || (!input.trim() && !attachedFile)) ? C.silverLo : C.silverHi,
                  fontSize: 16, flexShrink: 0,
                }}>
                  ▶
                </button>
              </div>
            </div>
          </>
        )}

        {/* Error outside chat */}
        {error && isCouncilMode && (
          <div style={{ padding: '8px 20px', color: '#e07070', fontSize: 12, fontFamily: font.body }}>
            {error}
          </div>
        )}
      </div>

      <style>{`
        @keyframes pulse { 0%,100%{opacity:.3} 50%{opacity:1} }
      `}</style>
    </div>
  )
}
